import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ICityPendingInvoiceFile, IPendingInvoiceFile } from "../../../../interfaces";
import useAuth from "../../../../hooks/useAuth";
import { SubmitHandler, useForm } from "react-hook-form";
import { EmitError } from "../../../../utils/EmitError";
import { AxiosError } from "axios";
import { UploadFile } from "antd";
import { EmitSuccessAlert } from "../../../../utils/EmitSuccessAlert";
import InvoicesService from "../../../../services/http/services/InvoicesService";
import { useNavigate } from "react-router-dom";

interface IChangeFileProps {
    onChangeHookForm: (event: any) => void,
    fileList: UploadFile<any>[]
}

const ACCEPTED_FILE_TYPES = [
    'application/xml',
    'text/xml',
    // 'application/json',
]


// const impostosMunicipais = ['IPTU'];

export default function useImportInvoiceForm() {
    const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
    const [pendingInvoicesFile, setPendingInvoicesFile] = useState<ICityPendingInvoiceFile[]>([]);
    const [hasPendingImports, setHasPendingImports] = useState(false);
    const [files, setFiles] = useState<UploadFile[]>([]);
    const { activeCity } = useAuth();
    const navigate = useNavigate();
    const refAbortController = useRef<AbortController | null>(null);


    const {
        handleSubmit,
        formState: { errors },
        control,
        setValue,
        clearErrors,
        setError
    } = useForm<IPendingInvoiceFile>({
        defaultValues: {
            type: 'NFS',
            county_id: activeCity ? activeCity.id : undefined,
            files: []
        },
        shouldFocusError: false
    });

    const handleClickViewInvoicePending = useCallback(() => {
        navigate(`/notas/pendente`);
    }, [navigate]);

    const loadPendingInvoiceFile = useCallback(async () => {
        try {
            refAbortController.current?.abort();

            refAbortController.current = new AbortController();

            const pendingInvoiceFileList = await InvoicesService.listCityPendingInvoiceFile(activeCity?.id!, refAbortController.current.signal);
            const hasPending = pendingInvoiceFileList.some(item => item.import_progress < 100);
            setHasPendingImports(hasPending);
            setPendingInvoicesFile(pendingInvoiceFileList);
        } catch (err) {
            if (err instanceof AxiosError && err.name === 'CanceledError') {
                return;
            }

            EmitError(err, true, 'pending_invoice_files_load');
        }
    }, [activeCity?.id]);

    const handleSubmitForm: SubmitHandler<IPendingInvoiceFile> = useCallback(async (data): Promise<boolean> => {
        try {
            setIsSubmiting(true);

            const importPendingInvoice = new FormData();

            files.forEach(file => {
                if (!file.originFileObj) {
                    return;
                }
                importPendingInvoice.append('files', file.originFileObj);
            });

            importPendingInvoice.append('type', data.type);
            importPendingInvoice.append('county_id', String(data.county_id));

            const newImportTaxeMessage = await InvoicesService.createPendingInvoice(importPendingInvoice);
            setValue('files', []);
            setHasPendingImports(true);
            EmitSuccessAlert(newImportTaxeMessage);
            setFiles([]);
            loadPendingInvoiceFile();

            return true;
        } catch (err) {
            EmitError(err);
            return false;
        } finally {
            setIsSubmiting(false);
        }
    }, [files, loadPendingInvoiceFile, setValue]);

    const dummyRequest = useCallback(({ onSuccess }: any) => {
        setTimeout(() => {
            onSuccess("ok");
        }, 0);
    }, []);

    const handleClickDownloadFile = useCallback(async (file_name: string) => {
        refAbortController.current?.abort();

        refAbortController.current = new AbortController();
        const fileUrl = await InvoicesService.downloadPendingInvoiceFile(file_name, refAbortController.current.signal);

        const link = document.createElement('a');
        link.download = file_name;
        link.target = '_blank';
        link.href = fileUrl;
        link.click();
    }, []);

    const handleChangeFile = useCallback(({ onChangeHookForm, fileList }: IChangeFileProps) => {

        fileList.forEach(file => {
            if (file?.type && !ACCEPTED_FILE_TYPES.includes(file.type)) {
                setError('files', { message: 'Formato de arquivo inválido! O formato aceito é .xml' });
                return;
            }
        });

        clearErrors('files');
        onChangeHookForm(fileList);
        setFiles(fileList);

    }, [setError, clearErrors]);


    useEffect(() => {
        loadPendingInvoiceFile();
    }, [loadPendingInvoiceFile]);

    useMemo(() => {
        if (!hasPendingImports) return;

        const interval = setInterval(() => {
            loadPendingInvoiceFile();
        }, 10000);

        return () => clearInterval(interval);
    }, [hasPendingImports, loadPendingInvoiceFile]);;



    return {
        handleSubmitForm,
        handleSubmit,
        control,
        isSubmiting,
        errors,
        ACCEPTED_FILE_TYPES,
        dummyRequest,
        handleChangeFile,
        files,
        pendingInvoicesFile,
        handleClickDownloadFile,
        handleClickViewInvoicePending,
    }
}
